/*
 * Copyright 2020 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

// Portions of the code in this file are based on code from react.
// Original licensing for the following can be found in the
// NOTICE file in the root directory of this source tree.
// See https://github.com/facebook/react/tree/cc7c1aece46a6b69b41958d731e0fd27c94bfc6c/packages/react-interactions

import type {RefObject, SyntheticEvent} from 'react';
import {useEffect, useRef} from 'react';

import {useEffectEvent} from '../utils';

export interface InteractOutsideProps {
  ref: RefObject<Element>;
  onInteractOutside?: (e: SyntheticEvent) => void;
  onInteractOutsideStart?: (e: SyntheticEvent) => void;
  /** Whether the interact outside events should be disabled. */
  isDisabled?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isValidEvent(event: any, ref: RefObject<Element>) {
  if (event.button > 0) {
    return false;
  }

  if (event.target) {
    // if the event target is no longer in the document, ignore
    const ownerDocument = event.target.ownerDocument;
    if (
      !ownerDocument ||
      !ownerDocument.documentElement.contains(event.target)
    ) {
      return false;
    }

    // If the target is within a top layer element (e.g. toasts), ignore.
    if (event.target.closest('[data-react-aria-top-layer]')) {
      return false;
    }
  }

  return ref.current && !ref.current.contains(event.target);
}

/**
 * Example, used in components like Dialogs and Popovers so they can close
 * when a user clicks outside them.
 */
export function useInteractOutside(props: InteractOutsideProps) {
  const {ref, onInteractOutside, isDisabled, onInteractOutsideStart} = props;
  const stateRef = useRef({
    isPointerDown: false,
    ignoreEmulatedMouseEvents: false,
  });

  const onPointerDown = useEffectEvent((e: SyntheticEvent) => {
    if (onInteractOutside && isValidEvent(e, ref)) {
      if (onInteractOutsideStart) {
        onInteractOutsideStart(e);
      }
      stateRef.current.isPointerDown = true;
    }
  });

  const triggerInteractOutside = useEffectEvent((e: SyntheticEvent) => {
    if (onInteractOutside) {
      onInteractOutside(e);
    }
  });

  useEffect(() => {
    const state = stateRef.current;
    const element = ref.current;
    const documentObject = element?.ownerDocument || document;

    if (isDisabled) {
      return;
    }

    // Use pointer events if available. Otherwise, fall back to mouse and touch events.
    if (typeof PointerEvent !== 'undefined') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const onPointerUp = (e: any) => {
        if (state.isPointerDown && isValidEvent(e, ref)) {
          triggerInteractOutside(e);
        }
        state.isPointerDown = false;
      };

      // changing these to capture phase fixed combobox
      documentObject.addEventListener('pointerdown', onPointerDown, true);
      documentObject.addEventListener('pointerup', onPointerUp, true);

      return () => {
        documentObject.removeEventListener('pointerdown', onPointerDown, true);
        documentObject.removeEventListener('pointerup', onPointerUp, true);
      };
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const onMouseUp = (e: any) => {
        if (state.ignoreEmulatedMouseEvents) {
          state.ignoreEmulatedMouseEvents = false;
        } else if (state.isPointerDown && isValidEvent(e, ref)) {
          triggerInteractOutside(e);
        }
        state.isPointerDown = false;
      };

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const onTouchEnd = (e: any) => {
        state.ignoreEmulatedMouseEvents = true;
        if (state.isPointerDown && isValidEvent(e, ref)) {
          triggerInteractOutside(e);
        }
        state.isPointerDown = false;
      };

      documentObject.addEventListener('mousedown', onPointerDown, true);
      documentObject.addEventListener('mouseup', onMouseUp, true);
      documentObject.addEventListener('touchstart', onPointerDown, true);
      documentObject.addEventListener('touchend', onTouchEnd, true);

      return () => {
        documentObject.removeEventListener('mousedown', onPointerDown, true);
        documentObject.removeEventListener('mouseup', onMouseUp, true);
        documentObject.removeEventListener('touchstart', onPointerDown, true);
        documentObject.removeEventListener('touchend', onTouchEnd, true);
      };
    }
  }, [ref, isDisabled, onPointerDown, triggerInteractOutside]);
}
